'use client'

import { useEffect, useRef } from 'react'

import { scheduleDomMeasure } from '@/utils/scheduleDomMeasure'

export function useSectionFooterOverflowMargin(footerGapPx: number) {
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const root = rootRef.current
    if (!root) {
      return
    }

    const section = root.closest('section')
    if (!section || !(section instanceof HTMLElement)) {
      return
    }

    const applyFooterMargin = () => {
      const sectionRect = section.getBoundingClientRect()
      const rootRect = root.getBoundingClientRect()
      const overflowBelowSection = Math.max(
        0,
        rootRect.bottom - sectionRect.bottom
      )
      const nextMargin = `${overflowBelowSection + footerGapPx}px`
      if (section.style.marginBottom !== nextMargin) {
        section.style.marginBottom = nextMargin
      }
    }

    let cancelScheduledApply = scheduleDomMeasure(applyFooterMargin)

    const scheduleApply = () => {
      cancelScheduledApply()
      cancelScheduledApply = scheduleDomMeasure(applyFooterMargin)
    }

    const resizeObserver = new ResizeObserver(scheduleApply)
    resizeObserver.observe(root)
    resizeObserver.observe(section)
    window.addEventListener('resize', scheduleApply, { passive: true })

    return () => {
      cancelScheduledApply()
      resizeObserver.disconnect()
      window.removeEventListener('resize', scheduleApply)
      section.style.marginBottom = ''
    }
  }, [footerGapPx])

  return rootRef
}
